import { Observable } from 'rxjs';
import { HttpOptions } from '../http-client';
import { CommonDataModel, ResponseFromServer } from '../../_models';

/**
 * Raw HTTP methods that bypass the server response envelope.
 * Use when an endpoint returns TResponse directly instead of ResponseFromServer<TResponse>.
 */
export interface IPlainHttpMethods {
  get<TResponse>(relativePath: string, options?: HttpOptions): Observable<TResponse>;

  post<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<TResponse>;

  put<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<TResponse>;

  delete<TResponse>(relativePath: string, options?: HttpOptions): Observable<TResponse>;

  patch<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<TResponse>;
}

export interface IServerHttpClient {
  /**
   * Raw HTTP methods for endpoints that return TResponse directly, without a ResponseFromServer envelope.
   * Prefer the top-level get/post/etc. methods for standard wrapped endpoints.
   */
  readonly plain: IPlainHttpMethods;
  /**
   * Makes a GET request and returns the full server response envelope.
   * @returns Observable<ResponseFromServer<TResponse>> - full server response with data and metadata
   */
  get<TResponse>(relativePath: string, options?: HttpOptions): Observable<ResponseFromServer<TResponse>>;

  /**
   * Makes a POST request and returns the full server response envelope.
   * @returns Observable<ResponseFromServer<TResponse>> - full server response with data and metadata
   */
  post<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<ResponseFromServer<TResponse>>;

  /**
   * Makes a PUT request and returns the full server response envelope.
   * @returns Observable<ResponseFromServer<TResponse>> - full server response with data and metadata
   */
  put<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<ResponseFromServer<TResponse>>;

  /**
   * Makes a DELETE request and returns the full server response envelope.
   * @returns Observable<ResponseFromServer<TResponse>> - full server response with data and metadata
   */
  delete<TResponse>(relativePath: string, options?: HttpOptions): Observable<ResponseFromServer<TResponse>>;

  /**
   * Makes a PATCH request and returns the full server response envelope.
   * @returns Observable<ResponseFromServer<TResponse>> - full server response with data and metadata
   */
  patch<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<ResponseFromServer<TResponse>>;

  /**
   * Makes a GET request and returns data wrapped in CommonDataModel.
   * @deprecated CommonDataModel is an anti-pattern. Use get() instead.
   * This method exists only for backward compatibility during migration.
   * New code should use get() and handle loading/error states at the component level.
   * @returns Observable<CommonDataModel<TResponse>> - data with loading/status metadata
   */
  getExpanded<TResponse>(relativePath: string, options?: HttpOptions): Observable<CommonDataModel<TResponse>>;

  /**
   * Makes a POST request and returns data wrapped in CommonDataModel.
   * @deprecated CommonDataModel is an anti-pattern. Use post() instead.
   * This method exists only for backward compatibility during migration.
   * New code should use post() and handle loading/error states at the component level.
   * @returns Observable<CommonDataModel<TResponse>> - data with loading/status metadata
   */
  postExpanded<TResponse, TBody>(relativePath: string, body: TBody, options?: HttpOptions): Observable<CommonDataModel<TResponse>>;
}
